import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  LuBraces, LuCircleCheck, LuExternalLink, LuImages, LuMessageSquareCode, LuMessagesSquare,
  LuShare2, LuTrash2, LuUndo2, LuUsers,
} from 'react-icons/lu';
import { API_BASE_URL } from '../../config.js';
import { formatParisInstant, formatReportRange, isSnapshot } from '../../utils/weekFormat.js';
import useAnalytics from '../../hooks/useAnalytics.jsx';
import useTabParam from '../../hooks/useTabParam.js';
import { useReportAnalyses } from '../../hooks/useReportAnalyses.js';
import { Card } from '../ui/Card';
import Button from '../ui/Button';
import DropdownMenu from '../ui/DropdownMenu';
import Alert from '../ui/Alert';
import Skeleton from '../ui/Skeleton';
import ErrorBoundary from '../ui/ErrorBoundary';
import { ErrorMessage } from '../ui/ErrorMessage';
import { WeekTile } from './WeeklyReportList';
import WeekSwitcher from './WeekSwitcher';
import ReportStatusBadges from './ReportStatusBadges';
import RawDumpModal from './RawDumpModal';
import ShareReportModal from './ShareReportModal';
import DeleteReportModal from './DeleteReportModal';
import PromptsModal from './PromptsModal';
import { ProcessingStatus } from './ProcessingStatus';
import { StatisticsSection } from './StatisticsSection';
import { AnalysesSection } from './AnalysesSection';
import { WeeklyGallery } from './WeeklyGallery';
import DumpSearch from './DumpSearch';
import { dumpFacts } from './dumpData.js';
import { DEFAULT_REPORT_TAB, getReportTabs } from './reportTabs.js';
import { analyzeProgressKey, isPublished, isReportProcessing } from './reportStatus.js';
import { withJail } from './jail.js';

const POLL_DELAY_MS = 5000;

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

/** "58 messages · 142 photos · 12 contributeurs" under the report title. */
const ReportFacts = ({ facts }) => {
  if (!facts) return <span />;
  const items = [
    { icon: LuMessagesSquare, value: facts.messages, label: facts.messages > 1 ? 'messages' : 'message' },
    { icon: LuImages, value: facts.pictures, label: facts.pictures > 1 ? 'photos' : 'photo' },
    { icon: LuUsers, value: facts.contributors, label: facts.contributors > 1 ? 'contributeurs' : 'contributeur' },
  ];
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-600 dark:text-gray-300">
      {items.map(({ icon: Icon, value, label }) => (
        <li key={label} className="inline-flex items-center gap-1.5">
          <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden />
          <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{value.toLocaleString('fr-FR')}</span>
          {label}
        </li>
      ))}
    </ul>
  );
};

export const WeeklyReport = ({ fetchWithAuth, currentUser }) => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    client, weeks, loading: clientLoading, isAdminUser, upsertWeek, removeWeek, setReportCounts, jail,
  } = useOutletContext();

  const [report, setReport] = useState(null);
  const [dump, setDump] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollAttempt, setPollAttempt] = useState(0);

  const [showRawDump, setShowRawDump] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [publicToken, setPublicToken] = useState(null);
  const [shareError, setShareError] = useState(null);

  // Kept here, not in AnalysesSection: the tabs unmount it, the selected analysis must survive.
  const [analysisType, setAnalysisType] = useState(null);

  const { track } = useAnalytics({ currentUser, report });

  useEffect(() => {
    if (analysisType) track('view_weekly_analysis', { analysis: analysisType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType]);

  const tabs = getReportTabs(isAdminUser);
  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);
  const [activeTab] = useTabParam(tabIds, DEFAULT_REPORT_TAB);

  const reportUrl = `${API_BASE_URL}/api/weekly/client/${slug}/week/${id}`;

  const loadDump = useCallback(async () => {
    const response = await fetchWithAuth(`${reportUrl}/dump`);
    setDump(await readJson(response));
  }, [fetchWithAuth, reportUrl]);

  // Initial load, once the layout has the client.
  useEffect(() => {
    if (clientLoading || !client) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setReport(null);
      setDump(null);
      try {
        const response = await fetchWithAuth(reportUrl);
        const reportData = await readJson(response);
        if (cancelled) return;
        if (!response.ok || !reportData?.id) {
          setError('Rapport introuvable ou erreur lors du chargement des données.');
          return;
        }
        // The API serves drafts to anyone with the link; the list never shows them to clients.
        if (!isAdminUser && !isPublished(reportData)) {
          setError('Ce rapport n\'est pas encore publié.');
          return;
        }
        setReport(reportData);
        setPublicToken(client.public_sharing_enabled && isPublished(reportData) ? reportData.public_token || null : null);
        track('view_weekly_report', { cockpit: reportData.client, week: `${reportData.year}-${reportData.week_number}` });
        if (reportData.steps?.dump === 'completed') await loadDump();
      } catch (err) {
        console.error('Erreur lors du chargement du rapport:', err);
        if (!cancelled) setError('Rapport introuvable ou erreur lors du chargement des données.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportUrl, clientLoading, client?.id]);

  // Poll while a step runs: each fresh report schedules the next check.
  useEffect(() => {
    if (!report || !isReportProcessing(report)) return undefined;
    const timer = setTimeout(async () => {
      try {
        const response = await fetchWithAuth(reportUrl);
        const fresh = await readJson(response);
        if (!response.ok || !fresh?.id) throw new Error('poll failed');
        if (report.steps?.dump !== 'completed' && fresh.steps?.dump === 'completed') await loadDump();
        setReport(fresh);
        upsertWeek(fresh);
      } catch (err) {
        console.error('Erreur lors du polling:', err);
        setPollAttempt((n) => n + 1);
      }
    }, POLL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [report, pollAttempt, fetchWithAuth, reportUrl, loadDump, upsertWeek]);

  const processing = isReportProcessing(report);
  const facts = useMemo(() => dumpFacts(dump, report, isAdminUser), [dump, report, isAdminUser]);

  // Loaded here, not in AnalysesSection: the sidebar counts them whatever the tab.
  const analysesStore = useReportAnalyses({
    enabled: !!report,
    fetchWithAuth,
    clientSlug: slug,
    weekId: id,
    refreshKey: analyzeProgressKey(report),
  });

  // Figures of the sidebar tabs: null until known (no pill rather than a wrong 0).
  const analysesCount = analysesStore.loadState === 'ready' ? analysesStore.analyses.length : null;
  const picturesCount = facts ? facts.pictures : null;
  useEffect(() => {
    setReportCounts({ reportId: id, analyses: analysesCount, pictures: picturesCount });
  }, [setReportCounts, id, analysesCount, picturesCount]);
  useEffect(() => () => setReportCounts((counts) => (counts?.reportId === id ? null : counts)), [setReportCounts, id]);

  const toggleReportStatus = async () => {
    try {
      setIsUpdatingStatus(true);
      setStatusUpdateError(null);
      const newStatus = isPublished(report) ? 'draft' : 'published';
      const response = await fetchWithAuth(`${reportUrl}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const updated = await readJson(response);
      if (!response.ok) throw new Error(updated.message || 'Erreur lors de la mise à jour du statut');
      setReport(updated);
      upsertWeek(updated);
      if (newStatus === 'draft') setPublicToken(null);
      else if (client?.public_sharing_enabled) setPublicToken(updated.public_token || null);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      setStatusUpdateError(err.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await fetchWithAuth(reportUrl, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await readJson(response);
        throw new Error(errorData.message || 'Erreur lors de la suppression du rapport');
      }
      removeWeek(id);
      navigate(withJail(`/w/${slug}`, jail));
    } catch (err) {
      console.error('Erreur lors de la suppression du rapport:', err);
      setDeleteError(err.message || 'Erreur lors de la suppression du rapport');
      setIsDeleting(false);
    }
  };

  const requestPublicToken = async (reset = false) => {
    setIsGeneratingShareLink(true);
    setShareError(null);
    try {
      const response = await fetchWithAuth(`${reportUrl}/generate-public-token${reset ? '?reset=true' : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await readJson(response);
      if (!response.ok) throw new Error(data.message || 'Erreur lors de la gestion du lien de partage');
      setPublicToken(reset ? null : data.public_token);
      upsertWeek({ ...report, public_token: reset ? null : data.public_token });
      return true;
    } catch (err) {
      console.error('Erreur lors de la gestion du lien de partage:', err);
      setShareError(err.message || 'Erreur lors de la gestion du lien de partage');
      return false;
    } finally {
      setIsGeneratingShareLink(false);
    }
  };

  const openShare = async () => {
    if (publicToken || await requestPublicToken(false)) setShowShareModal(true);
  };

  const disablePublicLink = async () => {
    if (await requestPublicToken(true)) setShowShareModal(false);
  };

  if (clientLoading || (loading && !error)) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !report) return <ErrorMessage message={error || 'Rapport introuvable'} title="Rapport indisponible" showHomeLink={!jail} />;

  const publicUrl = publicToken ? `${window.location.origin}/share/${id}/${publicToken}` : '';
  const sharingEnabled = client?.public_sharing_enabled === true;
  const published = isPublished(report);
  const reportLabel = `Semaine ${report.week_number} · ${report.year}`;
  // A snapshot is completed (and re-analysed) at the end of its period: publishing waits for it.
  const publishAfter = !isSnapshot(report) ? null
    : new Date(report.period_end) > Date.now() ? `Disponible après le ${formatParisInstant(report.period_end, { separator: ' à ' })}`
      : 'Disponible une fois les données récupérées';

  const menuItems = [
    published && { id: 'unpublish', label: 'Repasser en brouillon', description: 'Le rapport ne sera plus visible par le client.', icon: LuUndo2, onClick: toggleReportStatus, disabled: isUpdatingStatus || processing },
    publicToken && { id: 'open-link', label: 'Ouvrir le lien public', icon: LuExternalLink, href: publicUrl, external: true },
    sharingEnabled && !published && { note: 'Le partage public n\'est possible qu\'une fois le rapport publié.' },
    { divider: true },
    { id: 'raw', label: 'Données brutes', icon: LuBraces, onClick: () => setShowRawDump(true) },
    { id: 'prompts', label: 'Prompts d\'analyse', icon: LuMessageSquareCode, onClick: () => setShowPrompts(true) },
    { divider: true },
    { id: 'delete', label: 'Supprimer le rapport', icon: LuTrash2, tone: 'danger', onClick: () => { setDeleteError(null); setShowDeleteModal(true); } },
  ];

  return (
    <div className="space-y-4">
      <Card as="header">
        <div className="flex flex-wrap items-start justify-between gap-4 p-4 sm:p-5">
          <div className="flex min-w-0 items-start gap-4">
            <WeekTile week={report.week_number} large />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{client?.name}</p>
              <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">{reportLabel}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formatReportRange(report, { long: true })}</p>
              <div className="mt-2 flex flex-wrap gap-1.5 empty:hidden">
                <ReportStatusBadges report={report} isAdminUser={isAdminUser} hasPublicLink={!!publicToken} size="sm" />
              </div>
            </div>
          </div>
          <WeekSwitcher weeks={weeks} currentId={id} slug={slug} search={search} isAdminUser={isAdminUser} />
        </div>

        {(facts || isAdminUser) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-900 sm:px-5">
            <ReportFacts facts={facts} />
            {isAdminUser && (
              <div className="flex flex-wrap items-center gap-2">
                {!published && publishAfter && <span className="text-xs text-gray-500 dark:text-gray-400">{publishAfter}</span>}
                {!published && (
                  <Button
                    variant="primary"
                    icon={LuCircleCheck}
                    onClick={toggleReportStatus}
                    loading={isUpdatingStatus}
                    disabled={processing || !!publishAfter}
                    title={publishAfter || (processing ? 'Disponible une fois le traitement terminé' : 'Rendre le rapport visible par le client')}
                  >
                    Publier
                  </Button>
                )}
                {sharingEnabled && (
                  <Button
                    icon={LuShare2}
                    onClick={openShare}
                    loading={isGeneratingShareLink && !showShareModal}
                    disabled={!published}
                    title={published ? 'Partager le rapport par un lien public' : 'Publiez le rapport pour pouvoir le partager'}
                  >
                    Partager
                  </Button>
                )}
                <DropdownMenu label="Actions du rapport" items={menuItems} />
              </div>
            )}
          </div>
        )}
        {(statusUpdateError || (shareError && !showShareModal)) && (
          <div className="px-4 pb-4 sm:px-5">
            <Alert tone="danger">{statusUpdateError || shareError}</Alert>
          </div>
        )}
      </Card>

      <ProcessingStatus report={report} />

      <ErrorBoundary resetKeys={[activeTab, id]}>
        {activeTab === 'analyses' && (
          <AnalysesSection
            report={report}
            fetchWithAuth={fetchWithAuth}
            clientSlug={slug}
            weekId={id}
            client={client}
            isAdminUser={isAdminUser}
            currentUser={currentUser}
            activeType={analysisType}
            onActiveTypeChange={setAnalysisType}
            analysesStore={analysesStore}
          />
        )}
        {activeTab === 'gallery' && (
          <WeeklyGallery
            report={report}
            dump={dump}
            isAdminUser={isAdminUser}
            fetchWithAuth={fetchWithAuth}
            clientSlug={client?.slug}
            weekId={id}
            onHiddenPicturesChange={(ids) => setReport((r) => (r ? { ...r, hidden_pictures: { ids } } : r))}
          />
        )}
        {activeTab === 'stats' && isAdminUser && (
          <StatisticsSection report={report} fetchWithAuth={fetchWithAuth} clientSlug={slug} weekId={id} client={client} />
        )}
        {activeTab === 'search' && isAdminUser && <DumpSearch dump={dump} />}
      </ErrorBoundary>

      {isAdminUser && (
        <>
          <RawDumpModal isOpen={showRawDump} onClose={() => setShowRawDump(false)} clientSlug={slug} weekId={id} fetchWithAuth={fetchWithAuth} />
          <DeleteReportModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            error={deleteError}
            reportLabel={`${client?.name} · ${reportLabel}`}
          />
          <PromptsModal isOpen={showPrompts} onClose={() => setShowPrompts(false)} analysisConfig={client?.weekly_config?.analysis || []} />
          <ShareReportModal
            isOpen={showShareModal && !!publicToken}
            onClose={() => setShowShareModal(false)}
            publicUrl={publicUrl}
            onDisable={disablePublicLink}
            isDisabling={isGeneratingShareLink}
            error={shareError}
          />
        </>
      )}
    </div>
  );
};

/**
 * Route element of /w/:slug/:id. Keyed by report: switching week remounts the page, so no
 * state, pending fetch or poll of the previous week can land on the next one.
 */
export const WeeklyReportRoute = (props) => {
  const { id } = useParams();
  return <WeeklyReport key={id} {...props} />;
};

export default WeeklyReport;
